import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Space, Modal } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, DeleteOutlined, PoweroffOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Input from '../../components/input/Input';
import api from '../../services/api';
import { displayMessage } from '../../helpers/messages';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';

const { confirm } = Modal;

const DetailUser = () => {
  const navigate = useNavigate();
  const { idUser } = useParams();
  const { setShowLoading } = useContext(GeneralContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (idUser && idUser !== 'new') {
      setIsNew(false);
      loadUser();
    } else {
      setIsNew(true);
    }
  }, [idUser]);

  const loadUser = async () => {
    setLoading(true);
    try {
      const response = await api.user.get(idUser);
      setUserData(response.data);
      form.setFieldsValue(response.data);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_LOAD'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      if (isNew) {
        await api.user.create(values);
        showSuccessMessage(displayMessage('SUCCESS_CREATE'));
      } else {
        await api.user.update(idUser, values);
        showSuccessMessage(displayMessage('SUCCESS_UPDATE'));
      }
      navigate('/users');
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_GENERIC'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const handleDelete = () => {
    confirm({
      title: 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: `Deseja realmente excluir o usuário "${userData?.first_name} ${userData?.last_name}"? Esta ação não pode ser desfeita.`,
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setShowLoading(true);
          await api.user.delete(idUser);
          showSuccessMessage(displayMessage('SUCCESS_DELETE'));
          navigate('/users');
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_DELETE'));
        } finally {
          setShowLoading(false);
        }
      },
    });
  };

  const handleChangeActive = () => {
    const action = userData?.is_active ? 'inativar' : 'ativar';
    
    confirm({
      title: userData?.is_active ? displayMessage('CONFIRM_INACTIVATE') : displayMessage('CONFIRM_ACTIVATE'),
      icon: <ExclamationCircleOutlined />,
      content: `Deseja realmente ${action} o usuário "${userData?.first_name} ${userData?.last_name}"?`,
      okText: `Sim, ${action}`,
      okType: userData?.is_active ? 'danger' : 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setShowLoading(true);
          if (userData?.is_active) {
            await api.user.inactivate({ userIds: [idUser] });
          } else {
            await api.user.activate({ userIds: [idUser] });
          }
          showSuccessMessage(userData?.is_active ? displayMessage('SUCCESS_INACTIVATE') : displayMessage('SUCCESS_ACTIVATE'));
          // Recarregar dados do usuário
          await loadUser();
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_GENERIC'));
        } finally {
          setShowLoading(false);
        }
      },
    });
  };

  return (
    <Card
      title={isNew ? displayMessage('NEW_USER') : displayMessage('EDIT_USER')}
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          {displayMessage('BACK')}
        </Button>
      }
    >
      <Form
        form={form}
        onFinish={handleSave}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={displayMessage('FIRST_NAME')}
              name="first_name"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <Input placeholder="Digite o nome" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={displayMessage('LAST_NAME')}
              name="last_name"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <Input placeholder="Digite o sobrenome" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label={displayMessage('EMAIL')}
              name="email"
              rules={[
                { required: false },
                { type: 'email', message: displayMessage('ERROR_INVALID_EMAIL') }
              ]}
            >
              <Input placeholder="Digite o e-mail" type="email" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space>
            <Button onClick={handleBack}>
              {displayMessage('CANCEL')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={loading}
            >
              {displayMessage('SAVE')}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Botões de ações sensíveis - apenas para usuários existentes */}
      {!isNew && (
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Row gutter={16}>
            {/* <Col xs={24} sm={12} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button
                type={userData?.is_active ? 'default' : 'primary'}
                icon={<PoweroffOutlined />}
                onClick={handleChangeActive}
                style={{ minWidth: 200 }}
                size="large"
              >
                {userData?.is_active ? 'Inativar Usuário' : 'Ativar Usuário'}
              </Button>
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                {userData?.is_active 
                  ? 'Usuário não poderá mais acessar o sistema'
                  : 'Usuário voltará a ter acesso ao sistema'
                }
              </div>
            </Col> */}

            <Col xs={24} sm={12} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                style={{ minWidth: 200 }}
                size="large"
              >
                Excluir Usuário Permanentemente
              </Button>
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                Esta ação não pode ser desfeita
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
};

export default DetailUser;



